/**
 * Motor do "Próximos dias": índice, rodada fixada, o passo na tela, a camada
 * no mapa e a reprodução.
 *
 * - O `indice.json` é lido ao abrir o app (só ele: com o modo desligado nada
 *   mais desce) e de 10 em 10 min no modo, para avisar rodada nova.
 * - Ao entrar no modo a rodada mais nova COMPLETA de cada modelo é fixada; o
 *   mapa (e, na fase 2, a ficha) usam essa até o usuário trocar.
 * - Desenho: os valores do passo são interpolados (bilinear, com a máscara de
 *   sem dado — `interp.ts`) numa grade 4× mais fina, pintados pela paleta
 *   fixa e só então reprojetados para a grade do GOES pela tabela de vizinho
 *   mais próximo do radar (`radar/geometria.ts`). Interpolar cor criaria cores
 *   fora da legenda; interpolar valor, não. Grade de 544 × 448 px: ~5 ms na
 *   thread principal, sem worker.
 * - Memória: os passos baixados ficam como valores (~60 KB cada em Float32),
 *   no máximo `MAX_PASSOS_EM_CACHE`; só o passo atual vira imagem.
 */
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import VectorLayer from 'ol/layer/Vector';
import type OlMap from 'ol/Map';
import VectorSource from 'ol/source/Vector';
import Point from 'ol/geom/Point';
import { Fill, Stroke, Style, Text } from 'ol/style';
import { FrameImageLayer, FrameSource } from '../map/FrameLayer';
import { lonLatToMap, mapToLonLat } from '../map/projection';
import { Z } from '../map/zIndex';
import { isAbortError } from '../lib/fetchRetry';
import { isExportLocked, isPlaying, play, suspend } from '../loop/playback';
import { useLoopStore } from '../loop/store';
import { calcularGeometria, construirTabela, modeloLonLat, reprojetar, type GeometriaRadar } from '../radar/geometria';
import { juntarWn2 } from './acesso';
import { ATRIBUICAO_AIFS, ATRIBUICAO_CAMS, ATRIBUICAO_GFS, ATRIBUICAO_ICON, ATRIBUICAO_IFS, ATRIBUICAO_WN2, carregarIndice, carregarPasso, modeloTempoEmUso, type IndicePrevisao, type Modelo, type ModeloId, type ModeloTempo } from './api';
import { camadaDe, colorir, type CamadaPrevisao } from './camadas';
import { ampliar, extensaoLonLat } from './interp';
import { tempoEmUso, usePrevisaoStore } from './store';
import { horasDoIntervalo, instantesDaRodada, passoDoIntervalo, passoMaisProximo, primeiroPassoVisivel } from './tempo';

/** Quanto mais fina é a grade pintada (antes da reprojeção), nas grades de 0,25° (e nas mais grossas, como a do CAMS). */
export const FATOR = 4;

/** O fator de uma grade: a de 0,125° do ICON já é fina — 2× basta (a imagem fica do tamanho das outras). */
export function fatorDe(g: { passo: number }): number {
  return g.passo < 0.25 ? Math.max(1, Math.round((FATOR * g.passo) / 0.25)) : FATOR;
}
/**
 * Passos (campos de ~60 KB) guardados em memória: uma volta inteira da animação cabe. Os cenários do
 * WeatherNext 2 (61 passos) saem do servidor com `no-store` — sem isto, cada volta baixaria tudo de novo.
 */
const MAX_PASSOS_EM_CACHE = 96;
const INDICE_A_CADA_MS = 10 * 60_000;
/** Reprodução: tempo mínimo em cada passo. */
const PASSO_TOCANDO_MS = 700;

// ---------------------------------------------------------------------------
// Rodada e passo

/** A rodada em uso de um modelo: a fixada, se ainda está no disco; senão a mais nova. */
export function rodadaEmUso(m: Modelo | undefined, fixada: string | undefined): string | null {
  if (!m || m.rodadas.length === 0) return null;
  return fixada && m.rodadas.includes(fixada) ? fixada : m.rodadas[0];
}

export interface PassoNaTela {
  modelo: Modelo;
  rodada: string;
  instantes: number[];
  /** Índice do passo (−1 = sem previsão para este instante nesta camada). */
  k: number;
  horas: number;
}

export function passoNaTela(indice: IndicePrevisao | null, camada: CamadaPrevisao, rodadas: Partial<Record<ModeloId, string>>, t: number | null): PassoNaTela | null {
  const modelo = indice?.modelos[camada.modelo];
  const rodada = rodadaEmUso(modelo, rodadas[camada.modelo]);
  if (!modelo || !rodada || t === null) return null;
  const instantes = instantesDaRodada(modelo, rodada);
  let k: number;
  if (camada.tipo === 'intervalo') {
    k = passoDoIntervalo(instantes, t);
  } else {
    k = passoMaisProximo(instantes, t);
    // Mais de meio passo além do último: fora da previsão (o CAMS acaba em +120 h).
    const meio = instantes.length > 1 ? (instantes[instantes.length - 1] - instantes[instantes.length - 2]) / 2 : 0;
    if (k >= 0 && Math.abs(instantes[k] - t) > Math.max(meio, 3 * 3_600_000)) k = -1;
  }
  return { modelo, rodada, instantes, k, horas: k >= 0 ? horasDoIntervalo(modelo.passos, k) : 0 };
}

/** Instantes navegáveis do modo: os do modelo do tempo em uso (o eixo da barra), da rodada em uso. */
export function instantesDoModo(s: { indice: IndicePrevisao | null; rodadas: Partial<Record<ModeloId, string>>; tempo?: ModeloTempo }): number[] {
  const m = s.indice?.modelos[modeloTempoEmUso(s.tempo ?? 'ifs', s.indice)] ?? s.indice?.modelos.cams;
  const r = rodadaEmUso(m, m ? s.rodadas[m.id] : undefined);
  return m && r ? instantesDaRodada(m, r) : [];
}

// ---------------------------------------------------------------------------
// Índice

let ultimoIndice = 0;

/** Os modelos com rodada fixada no modo (o WeatherNext 2 só existe no índice com o acesso de pesquisa). */
const MODELOS_FIXADOS: readonly ModeloId[] = ['ifs', 'aifs', 'gfs', 'icon', 'cams', 'wn2'];

/** Os modelos que o aviso de "rodada nova" olha: o do tempo em uso, a fumaça e os cenários (rodada nova do outro modelo do tempo não avisa). */
function modelosEmUso(s: { tempo: ModeloTempo; indice: IndicePrevisao | null }): ModeloId[] {
  return [tempoEmUso(s), 'cams', 'wn2'];
}

async function atualizarIndice(): Promise<void> {
  const st = usePrevisaoStore.getState();
  if (st.situacao.estado === 'loading') return;
  usePrevisaoStore.setState({ situacao: { estado: 'loading', erro: null } });
  try {
    // O WeatherNext 2 só com o acesso de pesquisa (`acesso.ts`); sem ele, o índice é o público.
    const indice = await juntarWn2(await carregarIndice());
    ultimoIndice = Date.now();
    const s = usePrevisaoStore.getState();
    const nova = modelosEmUso({ tempo: s.tempo, indice }).some((id) => {
      const m = indice.modelos[id];
      return s.modo && s.rodadas[id] && m?.rodadas[0] && m.rodadas[0] > s.rodadas[id]!;
    });
    // Os cenários no link (ou na tela) sem o acesso: a chuva no lugar.
    const semCenarios = s.variavel === 'cenarios' && !indice.modelos.wn2;
    usePrevisaoStore.setState({ indice, situacao: { estado: 'ready', erro: null }, rodadaNova: nova, ...(semCenarios ? { variavel: 'chuva' as const } : {}) });
    if (s.modo) fixarRodadas(false);
  } catch (e) {
    usePrevisaoStore.setState({ situacao: { estado: 'error', erro: e instanceof Error ? e.message : String(e) } });
  }
}

/** Fixa as rodadas (ao entrar, ou "atualizar" no aviso); sem `trocar`, só preenche o que falta. */
export function fixarRodadas(trocar: boolean): void {
  const { indice, rodadas, tempo } = usePrevisaoStore.getState();
  if (!indice) return;
  const novas = { ...rodadas };
  for (const id of MODELOS_FIXADOS) {
    const m = indice.modelos[id];
    if (m?.rodadas[0] && (trocar || !novas[id] || !m.rodadas.includes(novas[id]!))) novas[id] = m.rodadas[0];
  }
  // "Rodada nova" só enquanto alguma fixada é mais velha que a mais nova no disco.
  const rodadaNova = modelosEmUso({ tempo, indice }).some((id) => {
    const r0 = indice.modelos[id]?.rodadas[0];
    return !!r0 && !!novas[id] && r0 > novas[id]!;
  });
  usePrevisaoStore.setState({ rodadas: novas, rodadaNova });
  ajustarInstante();
}

/** Leva o instante ao passo mais próximo disponível (link com instante fora da rodada, ou sem instante; empate: o mais cedo). */
function ajustarInstante(alvo?: number): void {
  const s = usePrevisaoStore.getState();
  const inst = instantesDoModo(s);
  if (!inst.length) return;
  const k = passoMaisProximo(inst, alvo ?? s.instante ?? Date.now());
  if (inst[k] !== s.instante) usePrevisaoStore.setState({ instante: inst[k] });
}

/**
 * O instante pedido antes da última troca de modelo e o que a troca pôs na
 * tela: IFS 09:00 → AIFS (06:00) → IFS volta às 09:00, e não às 06:00 —
 * desde que ninguém tenha andado na barra entre as trocas.
 */
let antesDaTroca: { pedido: number; posto: number | null } | null = null;

/** Troca o modelo do tempo (o botão "Modelo"): o instante vai ao passo mais próximo do novo modelo. */
export function trocarModeloTempo(tempo: ModeloTempo): void {
  const s = usePrevisaoStore.getState();
  if (s.tempo === tempo || s.exportando || isExportLocked()) return;
  const pedido = antesDaTroca && antesDaTroca.posto === s.instante ? antesDaTroca.pedido : s.instante;
  if (!s.modo || !s.indice) {
    antesDaTroca = null;
    usePrevisaoStore.setState({ tempo });
    return;
  }
  // Tudo de uma vez (o modelo, o instante, o aviso de rodada nova): uma pintura só, já no passo certo.
  const { indice } = s;
  const rodadas = { ...s.rodadas };
  for (const id of MODELOS_FIXADOS) {
    const m = indice.modelos[id];
    if (m?.rodadas[0] && (!rodadas[id] || !m.rodadas.includes(rodadas[id]!))) rodadas[id] = m.rodadas[0];
  }
  const inst = instantesDoModo({ indice, rodadas, tempo });
  let instante = s.instante;
  if (inst.length && pedido !== null) {
    // Chuva e cenários são do intervalo: o passo cuja janela contém o instante pedido (IFS 09h, a chuva
    // de 06–09 → AIFS 12h, a de 06–12); o resto, o passo mais próximo (empate: o mais cedo).
    const intervalo = camadaDe(s.variavel, s.fumaca, s.cenario, modeloTempoEmUso(tempo, indice)).tipo === 'intervalo';
    const ki = intervalo ? passoDoIntervalo(inst, pedido) : -1;
    instante = inst[ki >= 0 ? ki : passoMaisProximo(inst, pedido)]!;
  }
  const rodadaNova = modelosEmUso({ tempo, indice }).some((id) => {
    const r0 = indice.modelos[id]?.rodadas[0];
    return !!r0 && !!rodadas[id] && r0 > rodadas[id]!;
  });
  usePrevisaoStore.setState({ tempo, instante, rodadas, rodadaNova });
  antesDaTroca = pedido === null ? null : { pedido, posto: instante };
}

/** O loop das 48 h tocava quando o modo começou (volta a tocar ao sair). */
let loopTocava = false;

/** Entra ou sai do modo (o interruptor do trilho, o botão da barra e "◂ Observado"). */
export function definirModo(on: boolean): void {
  const s = usePrevisaoStore.getState();
  if (on === s.modo || s.exportando) return;
  if (on) {
    // Com a exportação em curso o modo não entra (ela grava as 48 h).
    if (isExportLocked()) return;
    // O play das 48 h para — sem apagar a preferência "estava tocando".
    loopTocava = isPlaying();
    suspend();
    usePrevisaoStore.setState({ modo: true, instante: null, rodadas: {}, rodadaNova: false });
    if (s.indice) fixarRodadas(true);
    if (!s.indice || Date.now() - ultimoIndice > INDICE_A_CADA_MS) void atualizarIndice();
  } else {
    // A ficha do ponto e o painel não sobrevivem ao modo (`setModo(false)` os fecha).
    usePrevisaoStore.setState({ pontoDoLink: null });
    usePrevisaoStore.getState().setModo(false);
    if (loopTocava) play();
    loopTocava = false;
  }
}

/** Anda `delta` passos na barra (‹ ›, setas; Home/End com ±∞). Nunca antes do primeiro passo que a barra mostra. */
export function andarPasso(delta: number): void {
  const s = usePrevisaoStore.getState();
  const inst = instantesDoModo(s);
  if (!inst.length) return;
  const primeiro = primeiroPassoVisivel(inst, Date.now());
  const k = Math.max(primeiro, passoMaisProximo(inst, s.instante ?? Date.now()));
  const n = Math.min(inst.length - 1, Math.max(primeiro, k + delta));
  usePrevisaoStore.setState({ instante: inst[n] });
}

/** Tocar/pausar a previsão (botão e espaço): no último passo, tocar recomeça do primeiro. */
export function alternarTocar(): void {
  const s = usePrevisaoStore.getState();
  if (s.tocando) return s.setTocando(false);
  const inst = instantesDoModo(s);
  if (inst.length < 2) return;
  if (passoMaisProximo(inst, s.instante ?? 0) >= inst.length - 1) s.setInstante(inst[primeiroPassoVisivel(inst, Date.now())]);
  s.setTocando(true);
}

/** O crédito exigido pela licença do modelo. */
export function atribuicaoDe(modelo: ModeloId): string {
  return { ifs: ATRIBUICAO_IFS, aifs: ATRIBUICAO_AIFS, gfs: ATRIBUICAO_GFS, icon: ATRIBUICAO_ICON, cams: ATRIBUICAO_CAMS, wn2: ATRIBUICAO_WN2 }[modelo];
}

// ---------------------------------------------------------------------------
// Camada no mapa

interface GeoModelo {
  geo: GeometriaRadar;
  tabela: Int32Array;
}

export class PrevisaoLayerManager {
  private readonly layer: FrameImageLayer;
  private readonly source: FrameSource;
  private readonly geos = new Map<ModeloId, GeoModelo>();
  private readonly cache = new Map<string, Promise<Float32Array>>();
  private readonly unsubs: Array<() => void> = [];
  private abort: AbortController | null = null;
  private seq = 0;
  private bitmap: ImageBitmap | null = null;
  /** Modelo e variável da imagem na fonte: trocar de variável não deixa a imagem da anterior "enquanto carrega". */
  private chaveNaTela = '';
  private tocarTimer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;
  /** Avisa a reprodução quando o passo pedido terminou de pintar (ou falhou). */
  private pintado: (() => void) | null = null;

  /** Contorno da célula da ficha aberta (traço claro sobre um escuro, legível sobre nuvem e noite). */
  private readonly fonteDestaque = new VectorSource();
  private readonly destaque = new VectorLayer({
    source: this.fonteDestaque,
    zIndex: Z.PREVISAO_DESTAQUE,
    className: 'ol-layer previsao-destaque',
    style: [
      new Style({ stroke: new Stroke({ color: 'rgba(8,13,24,0.85)', width: 4.5 }) }),
      new Style({ stroke: new Stroke({ color: '#c4b5fd', width: 2 }), fill: new Fill({ color: 'rgba(196,181,253,0.10)' }) }),
    ],
  });

  /** Setas do vento (só com a variável Vento): uma por grau, para onde o vento vai. */
  private readonly fonteSetas = new VectorSource();
  private readonly estilosSeta = new Map<number, Style>();
  private readonly setas = new VectorLayer({
    source: this.fonteSetas,
    zIndex: Z.PREVISAO_SETAS,
    className: 'ol-layer previsao-setas',
    // Com o mapa afastado as setas de 1° se encostariam: as que se sobrepõem saem.
    declutter: true,
    style: (f) => this.estiloSeta(f.get('angulo') as number),
  });

  constructor(private readonly map: OlMap) {
    this.source = new FrameSource([0, 0, 1, 1]);
    this.layer = new FrameImageLayer({ source: this.source, visible: false, className: 'ol-layer previsao-layer' });
    this.layer.setZIndex(Z.PREVISAO);
    this.layer.set('previsao', true);
    map.addLayer(this.layer);
    map.addLayer(this.destaque);
    map.addLayer(this.setas);
    this.unsubs.push(
      usePrevisaoStore.subscribe((a, b) => {
        if (a.ponto !== b.ponto || a.modo !== b.modo || a.indice !== b.indice || a.tempo !== b.tempo) this.desenharCelula();
      }),
    );
    this.unsubs.push(
      usePrevisaoStore.subscribe((a, b) => {
        if (a.opacidade !== b.opacidade) this.layer.setOpacity(a.opacidade);
        if (a.modo !== b.modo || a.instante !== b.instante || a.variavel !== b.variavel || a.fumaca !== b.fumaca || a.cenario !== b.cenario || a.tempo !== b.tempo || a.rodadas !== b.rodadas || a.indice !== b.indice) {
          this.refresh();
        }
        if (a.tocando !== b.tocando || a.modo !== b.modo) this.tocar();
      }),
    );
    this.layer.setOpacity(usePrevisaoStore.getState().opacidade);
    this.refresh();
  }

  private geometria(m: Modelo): GeoModelo {
    let g = this.geos.get(m.id);
    if (!g) {
      const f = fatorDe(m.grade);
      const px: [number, number] = [m.grade.nj * f, m.grade.ni * f];
      const geo = calcularGeometria(modeloLonLat(extensaoLonLat(m.grade)), px, lonLatToMap, mapToLonLat);
      g = { geo, tabela: construirTabela(geo, px[0], px[1]) };
      this.geos.set(m.id, g);
    }
    return g;
  }

  /**
   * Um campo num passo, do cache. O download NÃO leva o sinal de quem pediu:
   * a promessa fica no cache e é dividida por várias pinturas — cancelá-la
   * porque a primeira pintura foi trocada entregaria um cancelamento à
   * seguinte (e nada seria desenhado). Arquivos de ~30 KB: deixar terminar
   * custa pouco.
   */
  private campo(m: Modelo, rodada: string, campo: string, passo: number, semDado: number): Promise<Float32Array> {
    const chave = `${m.id}/${rodada}/${campo}/${passo}`;
    let p = this.cache.get(chave);
    if (!p) {
      const nova = carregarPasso(m, rodada, campo, passo, semDado);
      // Falha não fica no cache (a próxima vez tenta de novo) — só se ainda é ela que está lá.
      nova.catch(() => {
        if (this.cache.get(chave) === nova) this.cache.delete(chave);
      });
      p = nova;
      this.cache.set(chave, p);
      while (this.cache.size > MAX_PASSOS_EM_CACHE) this.cache.delete(this.cache.keys().next().value!);
    } else {
      // Recém-usado vai para o fim (o mais velho sai primeiro).
      this.cache.delete(chave);
      this.cache.set(chave, p);
    }
    return p;
  }

  refresh(): void {
    if (this.disposed) return;
    const s = usePrevisaoStore.getState();
    const camada = camadaDe(s.variavel, s.fumaca, s.cenario, tempoEmUso(s));
    const tela = s.modo ? passoNaTela(s.indice, camada, s.rodadas, s.instante) : null;
    this.layer.setVisible(!!tela && tela.k >= 0);
    this.source.setAttributions(atribuicaoDe(camada.modelo));
    if (!s.modo) {
      this.abort?.abort();
      ++this.seq;
      this.soltar();
      // Fora do modo os passos baixados não servem: memória de volta.
      this.cache.clear();
      return;
    }
    if (!tela || tela.k < 0) {
      // Cancela a pintura em andamento: ela não pode terminar e dizer "ok" (nem deixar a imagem).
      this.abort?.abort();
      ++this.seq;
      this.soltar();
      usePrevisaoStore.setState({ noPasso: 'sem' });
      this.pintado?.();
      return;
    }
    const chave = `${camada.modelo}/${camada.id}/${s.fumaca}/${s.cenario}`;
    if (chave !== this.chaveNaTela) {
      this.soltar();
      this.chaveNaTela = chave;
    }
    this.abort?.abort();
    const abort = new AbortController();
    this.abort = abort;
    const seq = ++this.seq;
    usePrevisaoStore.setState({ noPasso: 'carregando' });
    void this.pintar(tela, camada, s.indice!.semDado, abort.signal)
      .then(() => {
        if (seq !== this.seq) return;
        usePrevisaoStore.setState({ noPasso: 'ok' });
        // Já pede o passo seguinte (andar pela barra fica instantâneo) — só se esta pintura ainda vale.
        const prox = tela.k + 1;
        if (prox < tela.modelo.passos.length) {
          for (const c of camada.campos) void this.campo(tela.modelo, tela.rodada, c, tela.modelo.passos[prox], s.indice!.semDado).catch(() => {});
        }
      })
      .catch((e) => {
        if (isAbortError(e) || seq !== this.seq) return;
        console.warn('[AmaView] Falha na previsão:', e);
        usePrevisaoStore.setState({ noPasso: 'falhou' });
      })
      .finally(() => {
        if (seq === this.seq) this.pintado?.();
      });
  }

  private async pintar(tela: PassoNaTela, camada: CamadaPrevisao, semDado: number, signal: AbortSignal): Promise<void> {
    const { modelo, rodada, k, horas } = tela;
    const campos = await Promise.all(camada.campos.map((c) => this.campo(modelo, rodada, c, modelo.passos[k], semDado)));
    if (signal.aborted) return;
    const n = modelo.grade.ni * modelo.grade.nj;
    const valores = new Float32Array(n);
    for (let p = 0; p < n; p++) valores[p] = camada.valor(campos, p, horas);
    const fina = ampliar(valores, modelo.grade, fatorDe(modelo.grade));
    const cores = colorir(fina, camada.paleta);
    const { geo, tabela } = this.geometria(modelo);
    const saida = new ImageData(geo.largura, geo.altura);
    reprojetar(cores, tabela, new Uint32Array(saida.data.buffer));
    const bitmap = await createImageBitmap(saida);
    if (signal.aborted || this.disposed) {
      bitmap.close();
      return;
    }
    const antigo = this.bitmap;
    this.bitmap = bitmap;
    this.source.setFrameImage(bitmap, geo.extent);
    antigo?.close();
    this.desenharSetas(camada.id === 'vento' ? campos : null, modelo);
  }

  /** Estilo de uma seta (cacheado pelo ângulo em graus inteiros). */
  private estiloSeta(angulo: number): Style {
    const chave = Math.round((angulo * 180) / Math.PI);
    let s = this.estilosSeta.get(chave);
    if (!s) {
      s = new Style({
        text: new Text({
          text: '↑',
          font: 'bold 15px Inter, system-ui, sans-serif',
          rotation: (chave * Math.PI) / 180,
          fill: new Fill({ color: 'rgba(255,255,255,0.92)' }),
          stroke: new Stroke({ color: 'rgba(8,13,24,0.85)', width: 3 }),
        }),
      });
      this.estilosSeta.set(chave, s);
    }
    return s;
  }

  /**
   * Uma seta a cada 1° (4 pontos da grade de 0,25°), para onde o vento vai. O
   * ângulo sai de dois pontos projetados — na grade do GOES o norte não é
   * exatamente "para cima" em toda a Amazônia.
   */
  private desenharSetas(campos: readonly Float32Array[] | null, m: Modelo): void {
    this.fonteSetas.clear(true);
    if (campos && campos.length >= 2) {
      const [u, v] = campos as [Float32Array, Float32Array];
      const { lat0, lon0, passo, ni, nj } = m.grade;
      const salto = Math.max(1, Math.round(1 / passo));
      const feicoes: Feature[] = [];
      for (let i = Math.floor(salto / 2); i < ni; i += salto) {
        for (let j = Math.floor(salto / 2); j < nj; j += salto) {
          const uu = u[i * nj + j]!;
          const vv = v[i * nj + j]!;
          if (!Number.isFinite(uu) || !Number.isFinite(vv) || Math.hypot(uu, vv) < 0.5) continue;
          const lon = lon0 + j * passo;
          const lat = lat0 - i * passo;
          const a = lonLatToMap(lon, lat);
          const b = lonLatToMap(lon + uu * 0.01, lat + vv * 0.01);
          const f = new Feature({ geometry: new Point(a) });
          f.set('angulo', Math.atan2(b[0] - a[0], b[1] - a[1]));
          feicoes.push(f);
        }
      }
      this.fonteSetas.addFeatures(feicoes);
    }
    this.fonteSetas.changed();
  }

  /**
   * A célula em volta do ponto da ficha (~28 km; ~14 km no ICON — o passo do modelo do tempo): o valor é
   * de uma área, não do quarteirão. Cantos e meios dos lados — na projeção do
   * GOES as linhas de lon/lat não são retas no extremo da Amazônia.
   */
  private desenharCelula(): void {
    const s = usePrevisaoStore.getState();
    const { ponto, modo, indice } = s;
    this.fonteDestaque.clear(true);
    const passo = indice?.modelos[tempoEmUso(s)]?.grade.passo ?? 0.25;
    if (modo && ponto) {
      const [lon, lat] = ponto;
      const m = passo / 2;
      const anel = [
        [lon - m, lat - m],
        [lon, lat - m],
        [lon + m, lat - m],
        [lon + m, lat],
        [lon + m, lat + m],
        [lon, lat + m],
        [lon - m, lat + m],
        [lon - m, lat],
        [lon - m, lat - m],
      ].map(([x, y]) => lonLatToMap(x!, y!));
      this.fonteDestaque.addFeature(new Feature({ geometry: new Polygon([anel]) }));
    }
    this.fonteDestaque.changed();
  }

  private soltar(): void {
    this.chaveNaTela = '';
    this.fonteSetas.clear(true);
    this.source.setFrameImage(null);
    this.bitmap?.close();
    this.bitmap = null;
  }

  /** Reprodução: anda um passo quando o atual terminou de pintar e passou o tempo mínimo. */
  private tocar(): void {
    if (this.tocarTimer) clearTimeout(this.tocarTimer);
    this.tocarTimer = null;
    this.pintado = null;
    const s = usePrevisaoStore.getState();
    if (!s.modo || !s.tocando) return;
    const proximo = () => {
      const st = usePrevisaoStore.getState();
      if (!st.modo || !st.tocando) return;
      const inst = instantesDoModo(st);
      const k = passoMaisProximo(inst, st.instante ?? 0);
      if (k < 0 || k >= inst.length - 1) {
        st.setTocando(false);
        return;
      }
      let pronto = false;
      let tempo = false;
      const seguir = () => {
        if (pronto && tempo) proximo();
      };
      this.pintado = () => {
        pronto = true;
        seguir();
      };
      this.tocarTimer = setTimeout(() => {
        tempo = true;
        seguir();
      }, PASSO_TOCANDO_MS);
      st.setInstante(inst[k + 1]);
    };
    proximo();
  }

  dispose(): void {
    this.disposed = true;
    for (const u of this.unsubs) u();
    if (this.tocarTimer) clearTimeout(this.tocarTimer);
    this.abort?.abort();
    this.soltar();
    this.map.removeLayer(this.layer);
    this.map.removeLayer(this.destaque);
    this.map.removeLayer(this.setas);
  }
}

/** Índice ao abrir e, no modo, de 10 em 10 min. Devolve a função que para. */
export function iniciarIndice(): () => void {
  void atualizarIndice();
  const id = setInterval(() => {
    if (usePrevisaoStore.getState().modo && Date.now() - ultimoIndice >= INDICE_A_CADA_MS) void atualizarIndice();
  }, 60_000);
  // Link com `y`/`d`: a store já está no modo antes do índice; ao chegar, fixa as rodadas.
  const parar = usePrevisaoStore.subscribe((a, b) => {
    if (a.indice && a.indice !== b.indice && a.modo && Object.keys(a.rodadas).length === 0) fixarRodadas(true);
  });
  // No modo o loop das 48 h não toca escondido (o link abre direto no modo, e
  // a abertura pode dar play sozinha pela preferência "estava tocando").
  // Suspenso por aqui, o loop também volta a tocar ao sair do modo.
  const suspenderNoModo = () => {
    loopTocava = true;
    suspend();
  };
  const pararLoop = useLoopStore.subscribe((a) => {
    if (a.playing && usePrevisaoStore.getState().modo) suspenderNoModo();
  });
  if (usePrevisaoStore.getState().modo && useLoopStore.getState().playing) suspenderNoModo();
  return () => {
    clearInterval(id);
    parar();
    pararLoop();
  };
}
